import { Badge, Card, CardContent, CardHeader, CardTitle, Tabs, TabsContent, TabsList, TabsTrigger } from '@mlm/ui';
import { PageHeader } from './_shared';
import {
  AS_OF_LABEL,
  BALANCE_SHEET,
  INCOME_STATEMENT,
  PERIOD_LABEL,
  TRIAL_BALANCE,
  TRIAL_BALANCE_TOTALS,
  peso,
} from './sample-data';

function StatementRow({
  label,
  amount,
  bold,
  border,
  indent,
}: {
  label: string;
  amount: number;
  bold?: boolean;
  border?: boolean;
  indent?: boolean;
}) {
  return (
    <div
      className={
        'flex items-center justify-between py-2 text-sm ' +
        (border ? 'border-t ' : '') +
        (bold ? 'font-semibold ' : '') +
        (indent ? 'pl-4' : '')
      }
    >
      <span className={bold ? '' : 'text-muted-foreground'}>{label}</span>
      <span className="font-mono tabular-nums">{peso(amount)}</span>
    </div>
  );
}

export function StatementsPage() {
  const balanced = TRIAL_BALANCE_TOTALS.debit === TRIAL_BALANCE_TOTALS.credit;
  const sheetFoots =
    BALANCE_SHEET.totalAssets === BALANCE_SHEET.totalLiabilities + BALANCE_SHEET.totalEquity;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Financial Statements"
        subtitle={`${PERIOD_LABEL} · as of ${AS_OF_LABEL}`}
      />

      <Tabs defaultValue="tb">
        <TabsList>
          <TabsTrigger value="tb">Trial Balance</TabsTrigger>
          <TabsTrigger value="is">Income Statement</TabsTrigger>
          <TabsTrigger value="bs">Balance Sheet</TabsTrigger>
        </TabsList>

        <TabsContent value="tb">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle>Trial Balance</CardTitle>
              <Badge variant={balanced ? 'success' : 'destructive'}>
                {balanced ? 'In balance' : 'Out of balance'}
              </Badge>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="w-24 px-4 py-2">Code</th>
                    <th className="px-4 py-2">Account</th>
                    <th className="w-44 px-4 py-2 text-right">Debit</th>
                    <th className="w-44 px-4 py-2 text-right">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {TRIAL_BALANCE.map((r) => (
                    <tr key={r.code} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-1.5 font-mono text-xs">{r.code}</td>
                      <td className="px-4 py-1.5">{r.name}</td>
                      <td className="px-4 py-1.5 text-right font-mono tabular-nums">
                        {r.debit ? peso(r.debit) : '—'}
                      </td>
                      <td className="px-4 py-1.5 text-right font-mono tabular-nums">
                        {r.credit ? peso(r.credit) : '—'}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 font-semibold">
                    <td className="px-4 py-2" />
                    <td className="px-4 py-2">Totals</td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums">
                      {peso(TRIAL_BALANCE_TOTALS.debit)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums">
                      {peso(TRIAL_BALANCE_TOTALS.credit)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="is">
          <Card>
            <CardHeader>
              <CardTitle>Income Statement — {PERIOD_LABEL}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="pb-1 pt-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                Revenue
              </p>
              {INCOME_STATEMENT.revenue.map((r) => (
                <StatementRow key={r.label} label={r.label} amount={r.amount} indent />
              ))}
              <StatementRow label="Net sales" amount={INCOME_STATEMENT.netSales} bold border />

              <p className="pb-1 pt-4 text-[10px] uppercase tracking-wide text-muted-foreground">
                Other operating income
              </p>
              {INCOME_STATEMENT.otherIncome.map((r) => (
                <StatementRow key={r.label} label={r.label} amount={r.amount} indent />
              ))}
              <StatementRow
                label="Total revenue"
                amount={INCOME_STATEMENT.totalRevenue}
                bold
                border
              />

              <p className="pb-1 pt-4 text-[10px] uppercase tracking-wide text-muted-foreground">
                Cost of sales
              </p>
              <StatementRow label="Cost of goods sold" amount={INCOME_STATEMENT.cogs} indent />
              <StatementRow
                label="Gross profit"
                amount={INCOME_STATEMENT.grossProfit}
                bold
                border
              />

              <p className="pb-1 pt-4 text-[10px] uppercase tracking-wide text-muted-foreground">
                Operating expenses
              </p>
              {INCOME_STATEMENT.expenses.map((r) => (
                <StatementRow key={r.label} label={r.label} amount={r.amount} indent />
              ))}
              <StatementRow
                label="Total operating expenses"
                amount={INCOME_STATEMENT.totalExpenses}
                bold
                border
              />

              <div className="mt-2 flex items-center justify-between border-t-2 py-3 text-base font-semibold">
                <span>Net income</span>
                <span className="font-mono tabular-nums text-credit">
                  {peso(INCOME_STATEMENT.netIncome)}
                </span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bs">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle>Balance Sheet — as of {AS_OF_LABEL}</CardTitle>
              <Badge variant={sheetFoots ? 'success' : 'destructive'}>
                {sheetFoots ? 'In balance' : 'Out of balance'}
              </Badge>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="pb-1 pt-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                Assets
              </p>
              {BALANCE_SHEET.assets.map((r) => (
                <StatementRow key={r.label} label={r.label} amount={r.amount} indent />
              ))}
              <StatementRow label="Total assets" amount={BALANCE_SHEET.totalAssets} bold border />

              <p className="pb-1 pt-4 text-[10px] uppercase tracking-wide text-muted-foreground">
                Liabilities
              </p>
              {BALANCE_SHEET.liabilities.map((r) => (
                <StatementRow key={r.label} label={r.label} amount={r.amount} indent />
              ))}
              <StatementRow
                label="Total liabilities"
                amount={BALANCE_SHEET.totalLiabilities}
                bold
                border
              />

              <p className="pb-1 pt-4 text-[10px] uppercase tracking-wide text-muted-foreground">
                Equity
              </p>
              {BALANCE_SHEET.equity.map((r) => (
                <StatementRow key={r.label} label={r.label} amount={r.amount} indent />
              ))}
              <StatementRow label="Total equity" amount={BALANCE_SHEET.totalEquity} bold border />

              <div className="mt-2 flex items-center justify-between border-t-2 py-3 text-base font-semibold">
                <span>Total liabilities &amp; equity</span>
                <span className="font-mono tabular-nums">
                  {peso(BALANCE_SHEET.totalLiabilities + BALANCE_SHEET.totalEquity)}
                </span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
